import React from 'react';
import { Button } from '../components/ui/button';
import { Phone } from 'lucide-react';
import SEO from '../components/SEO';

const ChronicNeeds = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Chronic Needs Plans"
        description="Special Medicare plans for chronic conditions. Dual Special Needs Plans (D-SNP) and Chronic Condition Special Needs Plans (C-SNP) with extra benefits and coordinated care."
        keywords="Chronic needs plans, D-SNP, C-SNP, Special Needs Plans, Medicare chronic conditions, dual eligible, Medicare Medicaid, chronic disease management"
        path="/chronic-needs"
      />
      {/* Top Banner - Green */}
      <section className="py-2 px-4 bg-green-600">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-white text-sm font-semibold">
            Special Enrollment Periods may be available year-round
          </p>
        </div>
      </section>

      {/* Red Alert Banner */}
      <section className="py-3 px-4 bg-red-600">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-lg font-bold text-white">
            Did you know: You may be able to change your Medicare plan anytime during the year.
          </p>
        </div>
      </section>

      {/* Hero Section - Life Changes */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-2 text-gray-900">Life Changes.</h1>
              <h1 className="text-5xl lg:text-6xl font-bold mb-8 text-gray-900">So Can Your Medicare Plan.</h1>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">You May Be Able to Change Your Medicare Plan Anytime</h2>
              <p className="text-lg text-gray-800 mb-8 leading-relaxed">
                You may not have to wait for the fall enrollment season. If you have a qualifying chronic condition, move to a new area, or get approved for Medicaid or Extra Help, you may be able to change your Medicare plan now.
              </p>
              <div className="space-y-4">
                <Button asChild size="lg" className="w-full bg-teal-600 hover:bg-teal-700 text-white text-xl py-6">
                  <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                    Take the Survey
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="w-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-xl py-6">
                  <a href="tel:734.209.6712" className="flex items-center justify-center gap-2">
                    <Phone size={24} />
                    Call 734.209.6712
                  </a>
                </Button>
              </div>
              <p className="text-base text-gray-700 mt-4 text-center">No cost. No obligation. Let us help you.</p>
            </div>
            <div>
              <img 
                src="/images/chronic-needs.png"
                alt="Chronic condition Medicare plan information"
                className="rounded-lg w-full opacity-70"
              />
            </div>
          </div>
        </div>
      </section>

      {/* This Is What We Do Best - Blue to Teal Gradient */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">This Is What We Do Best</h2>
          <p className="text-xl text-white mb-8">
            We love helping seniors understand their real costs, find savings, and make sure they are not overpaying for their Medicare coverage.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-xl px-8 py-6">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Get Started
              </a>
            </Button>
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-6">
              <a href="#contact">
                Let Us Help You
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Chronic Condition Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Have a Chronic Condition? You May Qualify for a Medicare Plan Designed Especially for You
          </h2>
          <p className="text-xl text-center mb-12 text-gray-800">
            If you have one of the following, you may qualify for a plan designed specifically for your condition—and you may be able to make a change anytime.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Diabetes',
              'Cardiovascular Disease',
              'Chronic Heart Failure',
              'End-Stage Renal Disease (Dialysis)'
            ].map((condition, index) => (
              <div key={index} className="bg-cyan-100 border border-cyan-300 rounded-lg p-6 text-center">
                <p className="text-lg font-semibold text-gray-900">{condition}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section - Two Columns */}
      <section className="py-0 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left Column - Benefits */}
            <div className="bg-white p-12">
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Benefits of Qualifying</h3>
              <ul className="space-y-3 text-lg text-gray-800">
                <li>• Plans tailored to your specific condition</li>
                <li>• Coordinated care and condition-focused support</li>
                <li>• Prescription drug coverage designed around your needs</li>
                <li>• Potentially lower out-of-pocket costs</li>
                <li>• Extra benefits like dental, vision, and more (by plan)</li>
              </ul>
            </div>

            {/* Right Column - Make a Change */}
            <div className="bg-sky-400 p-12">
              <h4 className="text-3xl font-bold mb-6 text-white">Make a change anytime</h4>
              <p className="text-lg text-white mb-8">
                If you qualify for a Chronic Condition Special Needs Plan, you may not have to wait for the usual fall enrollment period. You may be able to change your Medicare plan now.
              </p>
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-6">
                <a href="/pdfs/chronic-needs.pdf" target="_blank" rel="noopener noreferrer">
                  Make a Change Anytime
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Action Buttons Below Benefits */}
      <section className="py-8 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-xl px-8 py-6">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Get Started
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-xl px-8 py-6">
              <a href="#contact">
                Let Us Help You
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Moving Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl font-bold mb-6 text-gray-900">Moving?</h2>
          <p className="text-xl mb-8 text-gray-800">
            If you move outside your current plan's service area, you may qualify for a Special Enrollment Period and be able to change your Medicare plan.
          </p>
          
          <ul className="space-y-3 text-lg text-gray-800 mb-8">
            <li>• Moving to a new county or state</li>
            <li>• Moving into or out of a plan's service area</li>
            <li>• Entering or leaving a nursing home or assisted living</li>
            <li>• Returning to the U.S. after living abroad</li>
          </ul>

          <div className="text-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-2xl px-12 py-8">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Take the Survey
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Medicaid/Extra Help Header */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">Approved for Medicaid or Extra Help?</h2>
          <p className="text-xl mb-4 text-gray-800">
            Extra Help is a program to reduce Rx drug prices through the Social Security Administration.
          </p>
          <p className="text-xl text-gray-800">
            If you qualify for Medicaid or Extra Help, that may open the door for a Medicare plan change.
          </p>
        </div>
      </section>

      {/* D-SNPs Section - Two Columns */}
      <section className="py-0 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left Column - D-SNPs */}
            <div className="bg-white p-12">
              <h3 className="text-3xl font-bold mb-4 text-gray-900">Dual Eligible Special Needs Plans (D-SNPs)</h3>
              <p className="text-lg mb-6 text-gray-800">
                These plans are designed for individuals who qualify for both Medicare and Medicaid.
              </p>
              <ul className="space-y-2 text-base text-gray-800">
                <li>• Lower out-of-pocket costs</li>
                <li>• Prescription drug coverage</li>
                <li>• Additional benefits like dental, vision, and hearing</li>
                <li>• Coordinated care between Medicare and Medicaid</li>
              </ul>
            </div>

            {/* Right Column - Why This Matters */}
            <div className="bg-green-200 p-12">
              <h4 className="text-3xl font-bold mb-6 text-gray-900">Why This Matters</h4>
              <p className="text-lg text-gray-800 mb-8">
                If you are approved for Medicaid or Extra Help, you may be eligible for a plan that provides more benefits and reduces your healthcare costs.
              </p>
              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-6">
                <a href="/pdfs/medicaid.pdf" target="_blank" rel="noopener noreferrer">
                  Why This Matters (Medicaid)
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="text-lg mb-4 text-gray-800">Contact us to learn if you qualify for a Special Enrollment Period.</p>
          <p className="text-3xl font-bold mb-8 text-gray-900">Call: 734.209.6712</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-xl px-8 py-6">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Take the Survey
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-xl px-8 py-6">
              <a href="#contact">
                Let Us Help You
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA - Teal to Green Gradient */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-700 to-green-700">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Find out if you qualify today</h2>
          <p className="text-xl mb-8 text-white">Take the survey, get started, and let us help you understand your Medicare options.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-xl px-8 py-6">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Get Started
              </a>
            </Button>
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-8 py-6">
              <a href="#contact">
                Let Us Help You
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChronicNeeds;
